/**
 * MicrosoftAuthentication Component
 * 
 * This component handles Microsoft OAuth authentication flow using PKCE (Proof Key for Code Exchange)
 * for enhanced security. It provides a button that initiates the Microsoft authentication flow in a popup
 * window and processes the returned authorization code.
 * 
 * Features:
 * - Implements PKCE flow for secure authentication
 * - Handles the authorization code exchange
 * - Stores authentication state for CSRF protection
 * - Uses a popup window for the authentication UI
 * - Communicates with the main window via postMessage API
 */
/* Aniket Bhesaniya */

import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { setProps, clearDetailsModalData } from '../../redux/actions';
import { store } from '../../index';
import { useNavigate } from 'react-router-dom';
import { Config, IISMethods } from '../../config/IISMethods';

const DEFAULT_SCOPES = [
  'offline_access',
  'https://graph.microsoft.com/Calendars.ReadWrite',
  'https://graph.microsoft.com/User.Read',
];

const MicrosoftAuthentication = (props) => {
  /* ---------------- basic helpers & constants ---------------- */
  const getCurrentState = () => store.getState();

  const OAUTH_ENDPOINTS = {
    AUTHORIZE: `https://login.microsoftonline.com/${
      props?.isSso ? 'organizations' : 'common'
    }/oauth2/v2.0/authorize`,
    TOKEN:
      'https://login.microsoftonline.com/common/oauth2/v2.0/token',
  };

  const CLIENT_ID =
    props.clientId || getCurrentState().MICROSOFT_CLIENT_ID;
  const REDIRECT_URI = Config.microsoftredirectionurl;
  const SCOPE = DEFAULT_SCOPES.join(' ');
  const navigate = useNavigate();


  /**
   * Converts a Uint8Array to a URL-safe base64 string
   * Used for PKCE code verifier and challenge generation
   * 
   * @param {Uint8Array} buffer - The buffer to encode
   * @returns {string} URL-safe base64 encoded string
   */
  const base64URLEncode = (buffer) =>
    btoa(String.fromCharCode.apply(null, buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  
  /**
   * Generates a code challenge from a code verifier using SHA-256
   * This is used in the PKCE flow to prevent authorization code interception attacks
   * 
   * @param {string} codeVerifier - The code verifier to hash
   * @returns {Promise<string>} The code challenge
   */
  const generateCodeChallenge = async (verifier) => {
    if (!window.crypto?.subtle)
      throw new Error('crypto.subtle unavailable');
    const digest = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(verifier),
    );
    return base64URLEncode(new Uint8Array(digest));
  };
  
  /**
   * Generates a random code verifier and its corresponding challenge
   * These are used in the PKCE flow to secure the authorization code exchange
   * 
   * @returns {Promise<{codeVerifier: string, codeChallenge: string}>} PKCE codes
   */
  const generatePKCECodes = async () => {
    // Generate random bytes for code verifier (32 bytes = 256 bits)
    const codeVerifier = base64URLEncode(
      crypto.getRandomValues(new Uint8Array(32)),
    );
    // Generate challenge by hashing the verifier
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  };

  /* ---------------- shared “store token” helper -------------- */
  const placeTokenInRedux = async (token) => {
    if (props?.ismeeting) {
      await props.clearDetailsModalData();
      await props.setProps({
        detailModalData: {
          token: { code: token, codeVerifier: '' },
        },
      });
    } else {
      await props.setProps({
        formData: {
          ...getCurrentState().formData,
          token: { code: token, codeVerifier: '' },
        },
      });
    }
  };

   /**
   * Process the authorization code received from Microsoft OAuth
   * This function is called when the popup window sends a message with the code
   * 
   * @param {string} code - The authorization code from Microsoft
   * @param {string} returnUrl - The URL to navigate to after processing
   */
  const processAuthCode = useCallback(
    async (code) => {
      await placeTokenInRedux(code);
      if (props.handleAddButtonClick) await props.handleAddButtonClick(code);
      IISMethods.clearLocalData('oauth_return_url');
      // Navigate to the target page
      navigate(`/${Config.pagenamelist['integrationpagename']}`)
    },
    [props, navigate],
  );

  /* listener & opener logic (unchanged) */
  useEffect(() => {
    /**
     * Handles messages sent from the authentication popup window
     * 
     * @param {MessageEvent} event - The message event containing auth data
     */
    const handleAuthMessage = (e) => {
      const { code,platform } = e.data || {};
      // Only process if this is from Microsoft
      if (platform !== 'Microsoft') return
      if (code) processAuthCode(code);
    };
    window.addEventListener('message', handleAuthMessage);
    /**
     * Checks if current page is the OAuth callback page and processes accordingly
     * This is needed for compatibility with popup handling
     */

    const isCallback = window.location.pathname.includes('/auth/callback');
    if (isCallback && window.opener) {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      window.opener.postMessage({ code,platform: 'Microsoft' }, '*');
      document.body.innerHTML =
        `
            <div style="text-align:center; margin-top:100px; font-family:Arial,sans-serif;">
              <h2>Authentication successful!</h2>
              <p>You can close this window now.</p>
            </div>
          `
      setTimeout(() => window.close(), 1500);
    }
    return () => window.removeEventListener('message', handleAuthMessage);
  }, [processAuthCode]);

  /* ---------------- main click handler ------------------------ */
  /**
   * Initiates the Microsoft OAuth login flow
   * Opens popup window with authorization URL
   */
  const initiateLogin = async () => {
  try {
    const { codeVerifier, codeChallenge } = await generatePKCECodes();
    const currentUrl = window.location.href;
    const userId = IISMethods.getCookiesData('loginInfo')?.uid;

    IISMethods.setLocalData('oauth_return_url', currentUrl);
    IISMethods.setSessionData('code_verifier', codeVerifier);
    IISMethods.setSessionData('oauth_nonce', userId);

    const stateObj = {
      returnUrl: currentUrl,
      timestamp: Date.now(),
      platform: 'Microsoft',
      nonce: userId,
    };
    const state = btoa(encodeURIComponent(JSON.stringify(stateObj)));
    IISMethods.setSessionData('auth_state', state);

    const authUrl = new URL(OAUTH_ENDPOINTS.AUTHORIZE);
    authUrl.searchParams.append('client_id', CLIENT_ID);
    authUrl.searchParams.append(
      'response_type',
      props.isSso ? 'token' : 'code'
    );
    authUrl.searchParams.append('redirect_uri', REDIRECT_URI);
    authUrl.searchParams.append('response_mode', props.isSso ? 'fragment' : 'query');
    authUrl.searchParams.append('scope', SCOPE);
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('prompt', 'select_account');

    // Only send PKCE challenge if using Authorization Code flow
    if (!props.isSso) {
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('code_challenge_method', 'S256');
      
    }


    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const windowFeatures = `width=${width},height=${height},top=${top},left=${left},` +
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`;

    window.open(authUrl.toString(), 'ms-oauth', windowFeatures);

  } catch (err) {
    console.error('initiateLogin error:', err);
  }
};


  /* ---------------- render ------------------------ */
  return React.cloneElement(props.children, {
    onClick: (e) => {
      e.preventDefault();
      initiateLogin();
    },
  });
};

/* ---------------- Redux bindings ------------------ */
export default connect(
  () => ({}),
  (dispatch) => ({
    setProps: (p) => dispatch(setProps(p)),
    clearDetailsModalData: (p) => dispatch(clearDetailsModalData(p)),
  }),
)(MicrosoftAuthentication);
